import { Replay } from "@mui/icons-material";
import { Box } from "@mui/material";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BriaButton from "../../../../../../../components/common/BriaButton/BriaButton";
import BriaButtonGroup from "../../../../../../../components/common/BriaButtonGroup/BriaButtonGroup";
import BriaCanvas, { MarkerPoint } from "../../../../../../../components/common/Galleries/BriaCanvas";
import { useAppStore } from "../../../../../../../hooks/useStores";
import { VideoFrameResult, getVideoFrame } from "../../../../../../../utils/videoUtils";
import PlaygroundVideo from "../../../../../../Playground/VideoGeneration/Video/PlaygroundVideo";
import { VIDEO_SEGMENTATION_ASYNC_PROPS } from "../../../../../../Playground/VideoGeneration/video-generation";
import { KeyPointIcon } from "../Config/KeyPoints/icons/KeyPoint";
import { RemoveKeyPointIcon } from "../Config/KeyPoints/icons/RemoveKeyPointIcon";
import { ActiveTab } from "../VideoEraserPopup";
import styles from "./VideoEraserPopupPreview.module.scss";

type VideoEraserPopupPreviewProps = {
	activeTab: ActiveTab | undefined;
	setActiveTab?: (tab: ActiveTab | undefined) => void;
	closePopup: () => void;
};

const VideoEraserPopupPreview = ({ activeTab, closePopup }: VideoEraserPopupPreviewProps) => {
	const { videoEditingStore, videoGenerationStore } = useAppStore();
	const { t } = useTranslation("video_editing", { keyPrefix: "lab.config.tools.erase.popup.preview" });
	const [videoFrame, setVideoFrame] = useState<VideoFrameResult | null>(null);

	const isMaskLoading = videoGenerationStore.isGeneratingMask;
	const maskToDisplay = videoGenerationStore.maskToDisplay;
	const disabledButtons = !maskToDisplay || isMaskLoading;

	useEffect(() => {
		const config = videoGenerationStore.segmentConfig;
		getVideoFrame(config.video, config.frame_index).then((frame) => {
			setVideoFrame(frame);
		});
	}, []);

	const handleApplyMask = () => {
		videoEditingStore.handleConfigChange("tool_config", {
			erase: { mask: videoGenerationStore.mask_url },
		});
		closePopup();
	};

	const handleResetMask = () => {
		videoEditingStore.handleConfigChange("tool_config", { erase: { mask: "" } });
		videoGenerationStore.mask_url = "";
		videoGenerationStore.merged_video_url = "";
	};

	const handlePointsChange = (points: MarkerPoint[]) => {
		runInAction(() => {
			// Currently using just the positive type key points
			videoGenerationStore.segmentConfig.key_points = points.map((point) => ({ ...point, type: "positive" }));
		});
	};

	const renderMainContent = () => {
		if (!maskToDisplay) {
			if (!isMaskLoading && activeTab === "keyPoints") {
				return (
					<BriaCanvas
						url={videoFrame?.imageSrc || ""}
						brushConfigs={videoGenerationStore.keypointBrushConfigs}
						pointProps={{
							points: videoGenerationStore.segmentConfig.key_points!,
							onPointsChange: handlePointsChange,
							addMarker: <KeyPointIcon />,
							removeMarker: <RemoveKeyPointIcon />,
						}}
					/>
				);
			} else if (!isMaskLoading) {
				return (
					<PlaygroundVideo
						src={videoGenerationStore.segmentConfig.video}
						className={styles.video}
						autoPlay={false}
						loading={isMaskLoading}
					/>
				);
			}
		}
		return (
			<PlaygroundVideo
				src={maskToDisplay}
				className={styles.video}
				loading={isMaskLoading}
				onTryAgain={() => videoGenerationStore.generateMaskByPrompt()}
				autoPlay={false}
				asyncProps={VIDEO_SEGMENTATION_ASYNC_PROPS.mask_by_prompt}
			/>
		);
	};

	return (
		<Box className={styles.container}>
			<Box className={styles.videoContainer}>
				{renderMainContent()}
				<BriaButtonGroup className={styles.actionButtons}>
					<BriaButton
						className={styles.actionButton}
						buttonType="textMedium"
						startIcon={<Replay />}
						onClick={handleResetMask}
						disabled={disabledButtons}
					>
						{t("resetMaskButton")}
					</BriaButton>
					<BriaButton
						className={styles.actionButton}
						buttonType="primaryMedium"
						onClick={handleApplyMask}
						disabled={disabledButtons}
					>
						{t("useMaskButton")}
					</BriaButton>
				</BriaButtonGroup>
			</Box>
		</Box>
	);
};

export default observer(VideoEraserPopupPreview);
